import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchCollection } from '../lib/api';
import { Users, Camera, Mail, Phone, User } from 'lucide-react';

// Mapping of employee names to their scraped photos
const EMPLOYEE_IMAGES = {
  "Anders M. Andersen": "https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/af05e7c9-57d7-4054-a5b8-9550739d556a.jpg",
  "Jesper A. Andersen": "https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/ba4303cc-c63b-4bc0-a641-b904a37dfb4c.jpg",
  "Jonas Nielsen": "https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/786ead26-5c23-44bf-8d21-a917c706c653.jpg",
  "Jesper Quorning Andersen": "https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/648fbee9-b18b-4d53-bb7b-19bc9b8bf26e.jpg",
  "Kasper Nielsen": "https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/b4ced38a-7adc-476a-b7e9-7ff8e73bbee3.jpg",
  "Gøsta Avlskarl": "https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/246f8e9e-ace6-4516-a091-02c5a6857c87.jpg",
  "Michael Kjærsgaard": "https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/a7c65781-3773-4fa6-ad9d-e7e198c706dc.jpg",
  "Kristian V. Nielsen": "https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/dfd5458e-6070-43bb-b2f9-828979521cd4.jpg",
  "Rasmus O. Steg": "https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/3a7c909a-5a15-43dd-897b-5049ed6c75fa.jpg",
  "Palle Bersang": "https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/90459fb1-5a92-4712-8617-cca9dd054066.png",
  "Christoffer August L Bejtrup": "https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/49073a45-b707-40fe-a035-96fe8d66ae8d.jpg",
  "Fabiano H de Lima Costa Souza": "https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/21dadd13-a71f-48cb-9b3e-476ee65c8505.png",
  "Kristoffer Halkjær Jørgensen": "https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/30407a0e-79f4-404f-b484-f63a79e3f4a2.jpg",
  "Marcus Ehlers Hebsgaard": "https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/4cf836ab-e0a5-4602-a041-8d0b4e15ead4.jpg",
  "Mads Vangsgaard Sørensen": "https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/9040f564-08e6-4f51-b10a-0f2c68686195.jpg",
  "Morten": "https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/18171d88-a125-4cb0-8c42-044b156b8190.jpg",
  "Lonnie Würtz": "https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/21dadd13-a71f-48cb-9b3e-476ee65c8505.png",
  "Palle Møberg Nielsen": "https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/dc831c5c-e130-4157-8e41-a78e16122682.jpg",
  "Dennis Würtz": "https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/e8026790-cf4b-40e5-a7f1-9da914b10c9d.jpg"
};

const PLACEHOLDER_IMAGE = "https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/21dadd13-a71f-48cb-9b3e-476ee65c8505.png";

export default function Employees() {
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    async function loadEmployees() {
      const data = await fetchCollection('medarbejdere');
      // Sort by order
      const sorted = (data || []).sort((a, b) => a.order - b.order);
      setEmployees(sorted);
    }
    loadEmployees();
  }, []);

  // Group employees by category
  const categories = ['Kontor', 'VVS montører', 'Blikkenslager', 'Lærlinge'];

  const getEmployeesByCategory = (cat) => {
    return employees.filter(emp => emp.category === cat);
  };

  return (
    <>
      <Helmet>
        <title>Medarbejdere – Møldrup VVS A/S</title>
        <meta name="description" content="Mød det professionelle og velkvalificerede team hos Møldrup VVS A/S. Vi står klar til at løse din VVS-opgave." />
        <html lang="da" />
      </Helmet>

      {/* Hero Banner */}
      <section className="relative bg-slate-950 text-white py-24 md:py-32 border-b border-slate-900">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/0cd8fe7f-0ba4-47e3-908a-4fc623bd309c.jpg"
            alt="Medarbejdere"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-12">
          <span className="text-blue-500 text-xs font-extrabold tracking-[0.25em] uppercase block mb-3">
            Vores Team
          </span>
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-wider uppercase">
            Medarbejdere
          </h1>
          <p className="mt-4 text-sm md:text-base text-gray-400 max-w-2xl leading-relaxed">
            Mød vores velkvalificerede og ansvarsbevidste medarbejdere, der altid udfører arbejdet med stor faglig stolthed.
          </p>
        </div>
      </section>

      {/* Employees List Section */}
      <section className="py-24 bg-white text-slate-900">
        <div className="max-w-7xl mx-auto px-6 space-y-16">
          {categories.map((cat) => {
            const catEmployees = getEmployeesByCategory(cat);
            if (catEmployees.length === 0) return null;

            return (
              <div key={cat} className="space-y-8">
                {/* Category Header */}
                <div className="border-b border-gray-200 pb-3">
                  <h2 className="text-xl md:text-2xl font-extrabold uppercase tracking-wider text-slate-800">
                    {cat}
                  </h2>
                </div>

                {/* Employees Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {catEmployees.map((emp) => {
                    const imageUrl = EMPLOYEE_IMAGES[emp.name];
                    const hasValidImage = imageUrl && imageUrl !== PLACEHOLDER_IMAGE;

                    return (
                      <div
                        key={emp.id || emp.order}
                        className="bg-slate-50 border border-gray-100 flex flex-col justify-between rounded-none"
                      >
                        <div>
                          {/* Employee Image or Placeholder */}
                          <div className="relative w-full h-72 bg-slate-100 overflow-hidden border-b border-gray-100">
                            {hasValidImage ? (
                              <img
                                src={imageUrl}
                                alt={emp.name}
                                className="w-full h-full object-cover"
                                style={{ filter: 'grayscale(0.7)' }}
                              />
                            ) : (
                              <div className="w-full h-full flex flex-col items-center justify-center bg-slate-100 text-slate-400 p-6">
                                <div className="w-16 h-16 bg-slate-200 flex items-center justify-center text-slate-600 font-extrabold text-lg uppercase tracking-wider mb-3 rounded-none">
                                  {emp.name.split(' ').map(n => n[0]).slice(0, 2).join('')}
                                </div>
                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                  Billede på vej
                                </span>
                              </div>
                            )}
                          </div>

                          <div className="p-6 space-y-2">
                            <h3 className="font-extrabold uppercase tracking-wider text-sm text-slate-900">
                              {emp.name}
                            </h3>
                            <p className="text-xs text-blue-600 font-semibold uppercase tracking-wider">
                              {emp.role}
                            </p>
                          </div>
                        </div>

                        {/* Contact details for specific roles if available */}
                        {emp.name === 'Palle Møberg Nielsen' && (
                          <div className="px-6 pb-6 pt-4 border-t border-gray-100 flex items-center space-x-2 text-xs text-gray-500">
                            <Mail className="w-4 h-4 text-blue-500 shrink-0" />
                            <a href="mailto:palle.n@moeldrupvvs.dk" className="hover:text-blue-600 transition-colors duration-150 whitespace-nowrap font-medium">
                              palle.n@moeldrupvvs.dk
                            </a>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}
